import React, { useEffect, useState } from "react";
import TitleBar from "@common/components/TitleBar/TitleBar";
import {
  searchSharedRecipe,
  getSharedAuthors,
} from "@beers/services/recipeService";
import Spinner from "@common/components/Spinner/Spinner";
import ButtonWithLoad from "@common/components/ButtonWithLoad/ButtonWithLoad";
import { get } from "@common/services/request";
import { FormGroup, FormControl, Row, Modal, Button } from "react-bootstrap";
import ContentWrapper from "@common/components/ContentWrapper/ContentWrapper";
import styled from "styled-components";
import RecipeListItemSquare from "./RecipeListItemSquare";

const Padding = styled.div`
  padding: 20px 0px;
`;

const StyleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: ${(props) =>
    props.active === "true" ? "2px solid #e0a800" : "1px solid #ddd"};
  background: ${(props) => (props.active === "true" ? "#fff8e1" : "#fff")};
  cursor: pointer;
  &:hover {
    border-color: #e0a800;
  }
`;

const SrmDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => props.color || "#e0a800"};
`;

const loadStates = { loading: 0, success: 1, fail: 2 };
const paginationLoadStates = { idle: 0, loading: 1, fail: 3 };

let nextRecipes = null;

const srmColors = {
  "American IPA": "#c8720a",
  "New England IPA": "#e8c96e",
  "Belgian Tripel": "#f0d060",
  "American Pale Ale": "#e8a030",
  Weissbier: "#f0e080",
  Saison: "#e8c840",
  "Belgian Blond Ale": "#e0b830",
  "Double IPA": "#c06010",
  "German Pils": "#f0e090",
  "Irish Stout": "#1a0a00",
  "Oatmeal Stout": "#2a1000",
  "English IPA": "#c87010",
  "Russian Imperial Stout": "#100500",
};

const SharedRecipes = () => {
  const savedState = sessionStorage.getItem("communityPageState");
  const parsed = savedState ? JSON.parse(savedState) : null;

  const [loadState, setLoadState] = useState(loadStates.loading);
  const [paginationLoadState, setPaginationLoadState] = useState(
    paginationLoadStates.idle,
  );
  const [recipes, setRecipes] = useState(
    parsed && parsed.recipes ? parsed.recipes : [],
  );
  const [searchTerm, setSearchTerm] = useState(parsed ? parsed.searchTerm : "");
  const [activeStyle, setActiveStyle] = useState(
    parsed ? parsed.activeStyle : "All",
  );
  const [styleCounts, setStyleCounts] = useState({});
  const [sortOrder, setSortOrder] = useState(
    parsed && parsed.sortOrder ? parsed.sortOrder : "-favs",
  );
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [abvFilter, setAbvFilter] = useState(
    parsed && parsed.abvFilter ? parsed.abvFilter : "",
  );
  const [durationFilter, setDurationFilter] = useState(
    parsed && parsed.durationFilter ? parsed.durationFilter : "",
  );
  const [authorFilter, setAuthorFilter] = useState(
    parsed && parsed.authorFilter ? parsed.authorFilter : "",
  );
  const [authors, setAuthors] = useState([]);
  const [retryCount, setRetryCount] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [skipNextFetch, setSkipNextFetch] = useState(!!parsed);

  useEffect(() => {
    if (parsed && parsed.recipes && parsed.recipes.length > 0) {
      setLoadState(loadStates.success);
      nextRecipes = parsed.nextRecipes;
      setTimeout(() => window.scrollTo(0, parsed.scrollY || 0), 800);
    }
    getSharedAuthors()
      .then((res) => setAuthors(res))
      .catch(() => {});

    get("v1/shared_recipes/style_facets/")
      .then((response) => {
        const countsObj = {};
        response.forEach((styleObj) => {
          countsObj[styleObj.name] = styleObj.count;
        });
        setStyleCounts(countsObj);
      })
      .catch(() => {});

    setInitialized(true);
  }, []);

  useEffect(() => {
    if (!initialized) return;
    if (skipNextFetch) {
      setSkipNextFetch(false);
      return;
    }
    if (searchTerm.length > 0 && searchTerm.length < 3) return;
    const timer = setTimeout(() => {
      const combined =
        activeStyle === "All"
          ? searchTerm
          : searchTerm
            ? searchTerm + " " + activeStyle
            : activeStyle;
      setLoadState(loadStates.loading);

      let filters = {};
      if (abvFilter === "<5") {
        filters.abv_max = 4.9;
      }
      if (abvFilter === "5-8") {
        filters.abv_min = 5;
        filters.abv_max = 7.9;
      }
      if (abvFilter === "8-10") {
        filters.abv_min = 8;
        filters.abv_max = 9.9;
      }
      if (abvFilter === ">10") {
        filters.abv_min = 10;
      }

      if (durationFilter === "<14") {
        filters.days_max = 13;
      }
      if (durationFilter === "14-21") {
        filters.days_min = 14;
        filters.days_max = 20;
      }
      if (durationFilter === ">21") {
        filters.days_min = 21;
      }
      if (authorFilter) {
        filters.author = authorFilter;
      }

      searchSharedRecipe(combined, sortOrder, filters)
        .then((response) => {
          setRecipes(response.results);
          nextRecipes = response.next;
          setLoadState(loadStates.success);
          sessionStorage.setItem(
            "communityPageState",
            JSON.stringify({
              recipes: response.results,
              nextRecipes: response.next,
              searchTerm,
              activeStyle,
              sortOrder,
              abvFilter,
              durationFilter,
              authorFilter,
              scrollY: 0,
            }),
          );
        })
        .catch(() => setLoadState(loadStates.fail));
    }, 500);
    return () => timer && clearTimeout(timer);
  }, [
    searchTerm,
    activeStyle,
    sortOrder,
    abvFilter,
    durationFilter,
    authorFilter,
    retryCount,
    initialized,
  ]);

  useEffect(() => {
    const saveScroll = () => {
      const saved = sessionStorage.getItem("communityPageState");
      if (saved) {
        const state = JSON.parse(saved);
        state.scrollY = window.scrollY;
        sessionStorage.setItem("communityPageState", JSON.stringify(state));
      }
    };
    window.addEventListener("beforeunload", saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener("beforeunload", saveScroll);
    };
  }, []);

  const loadMore = () => {
    setPaginationLoadState(paginationLoadStates.loading);
    const url = nextRecipes.split(".io/")[1];
    get(url)
      .then((response) => {
        nextRecipes = response.next;
        const newRecipes = [...recipes, ...response.results];
        setRecipes(newRecipes);
        setPaginationLoadState(paginationLoadStates.idle);
        sessionStorage.setItem(
          "communityPageState",
          JSON.stringify({
            recipes: newRecipes,
            nextRecipes: response.next,
            searchTerm,
            activeStyle,
            sortOrder,
            abvFilter,
            durationFilter,
            authorFilter,
            scrollY: window.scrollY,
          }),
        );
      })
      .catch(() => setPaginationLoadState(paginationLoadStates.fail));
  };

  const handleChange = (e) => {
    setInitialized(true);
    setSearchTerm(e.target.value);
  };

  const handleStyleClick = (style) => {
    setInitialized(true);
    setActiveStyle(style);
  };

  const loadedAll = nextRecipes === null;
  const isLoading = paginationLoadState === paginationLoadStates.loading;

  return (
    <>
      <TitleBar title="COMMUNITY" subtitle="Recipes shared by the community" />
      <div className="recipe__searchbar community-searchbar-container">
        <div className="col-lg-8 col-lg-offset-2 col-md-12">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <FormGroup
              controlId="signinForm"
              className="recipe__searchbar__input beer-library__search-wrapper"
              style={{ margin: 0, flex: 1, maxWidth: "100%" }}
            >
              <span className="ion-search beer-library__search-icon" />
              <FormControl
                name="searchbar"
                type="text"
                value={searchTerm}
                placeholder="Search recipes, styles, ingredients, or brewers"
                onChange={handleChange}
                autoFocus={true}
              />
              {searchTerm && (
                <i
                  className="ion-close-round search-clear-icon"
                  onClick={() => {
                    setSearchTerm("");
                    setInitialized(true);
                  }}
                />
              )}
            </FormGroup>
          </div>
          <div className="filters-sort-container">
            <Button
              onClick={() => setShowFilterModal(true)}
              className={`filter-modal-btn ${abvFilter || durationFilter || authorFilter ? "attention" : ""}`}
            >
              <i className="ion-android-options filter-icon" /> Filters{" "}
              {abvFilter || durationFilter || authorFilter
                ? "(" +
                  ((abvFilter ? 1 : 0) +
                    (durationFilter ? 1 : 0) +
                    (authorFilter ? 1 : 0)) +
                  ")"
                : ""}
            </Button>
            <div className="sort-wrapper">
              <span className="sort-label">Sort by:</span>
              <FormGroup controlId="sortOrderForm" className="sort-form-group">
                <FormControl
                  componentClass="select"
                  value={sortOrder}
                  onChange={(e) => {
                    setInitialized(true);
                    setSortOrder(e.target.value);
                  }}
                  className="sort-select"
                >
                  <option value="-favs">Most Liked</option>
                  <option value="-created_at">Newest First</option>
                  <option value="created_at">Oldest First</option>
                  <option value="beer__name">Name (A-Z)</option>
                  <option value="beer__brewer__display_name">
                    Brewer (A-Z)
                  </option>
                  <option value="-ibu">IBU (High-Low)</option>
                  <option value="ibu">IBU (Low-High)</option>
                  <option value="-abv">ABV (High-Low)</option>
                  <option value="abv">ABV (Low-High)</option>
                </FormControl>
              </FormGroup>
            </div>
          </div>
          <div className="style-counts-grid">
            {[
              "All",
              ...Object.entries(styleCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
                .map((e) => e[0]),
            ].map((style) => (
              <StyleBlock
                key={style}
                active={activeStyle === style ? "true" : "false"}
                onClick={() => handleStyleClick(style)}
              >
                {style !== "All" && (
                  <SrmDot color={srmColors[style] || "#e0a800"} />
                )}
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#333",
                    }}
                  >
                    {style}
                  </div>
                  {style !== "All" && styleCounts[style] && (
                    <div style={{ fontSize: "11px", color: "#999" }}>
                      {styleCounts[style]} recipes
                    </div>
                  )}
                </div>
              </StyleBlock>
            ))}
          </div>
        </div>
      </div>
      <ContentWrapper>
        <Padding>
          {loadState === loadStates.loading ? (
            <Spinner />
          ) : loadState === loadStates.success ? (
            <div className="flex flex-column items-center">
              <Row style={{ width: "100%" }}>
                {recipes.map((recipe) => (
                  <div
                    key={recipe.id}
                    onClick={() => {
                      const saved =
                        sessionStorage.getItem("communityPageState");
                      if (saved) {
                        const state = JSON.parse(saved);
                        state.scrollY = window.scrollY;
                        sessionStorage.setItem(
                          "communityPageState",
                          JSON.stringify(state),
                        );
                      }
                    }}
                  >
                    <RecipeListItemSquare recipe={recipe} />
                  </div>
                ))}
              </Row>
              {recipes.length === 0 && <div>No recipes found</div>}
              <Padding>
                <ButtonWithLoad
                  isLoading={isLoading}
                  disabled={loadedAll}
                  onClick={loadMore}
                >
                  {loadedAll ? "No more recipes" : "Show more"}
                </ButtonWithLoad>
              </Padding>
            </div>
          ) : loadState === loadStates.fail ? (
            <div className="flex flex-column items-center">
              <span>Something went wrong</span>
              <ButtonWithLoad onClick={() => setRetryCount((prev) => prev + 1)}>
                Retry
              </ButtonWithLoad>
            </div>
          ) : null}
        </Padding>
      </ContentWrapper>

      <Modal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        dialogClassName="modal-mobile-bottom"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-filter-title">
            Advanced Filters
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="filter-section-container">
            <h5 className="modal-filter-section-title">Author</h5>
            <FormGroup controlId="authorFilterForm">
              <FormControl
                componentClass="select"
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
                className="sort-select"
              >
                <option value="">Any Author</option>
                {authors.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.display_name}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </div>
          <div className="filter-section-container">
            <h5 className="modal-filter-section-title">ABV (%)</h5>
            <div className="filter-chips-container">
              {[
                { label: "< 5%", value: "<5" },
                { label: "5 - 8%", value: "5-8" },
                { label: "8 - 10%", value: "8-10" },
                { label: "> 10%", value: ">10" },
              ].map((opt) => (
                <div
                  key={opt.value}
                  onClick={() =>
                    setAbvFilter(abvFilter === opt.value ? "" : opt.value)
                  }
                  className={`filter-chip-selectable ${abvFilter === opt.value ? "attention" : ""}`}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          </div>
          <div>
            <h5 className="modal-filter-section-title">Duration (Days)</h5>
            <div className="filter-chips-container">
              {[
                { label: "< 14 days", value: "<14" },
                { label: "14 - 21 days", value: "14-21" },
                { label: "> 21 days", value: ">21" },
              ].map((opt) => (
                <div
                  key={opt.value}
                  onClick={() =>
                    setDurationFilter(
                      durationFilter === opt.value ? "" : opt.value,
                    )
                  }
                  className={`filter-chip-selectable ${durationFilter === opt.value ? "attention" : ""}`}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setAbvFilter("");
              setDurationFilter("");
              setAuthorFilter("");
            }}
            className="btn-clear-all"
          >
            Clear All
          </Button>
          <Button
            onClick={() => {
              setInitialized(true);
              setShowFilterModal(false);
            }}
            className="btn-apply-filters"
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SharedRecipes;
